<template>
  <HeadTitle
    title="Crear grupo de documentos"
    description="Formulario para crear un nuevo grupo de documentos "
  />
  <br />
  <div v-if="isLoading" class="grid w-10 mt-4">
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <div class="flex flex-column md:col-6">
        <Skeleton width="100%" height="2.5rem" class="mb-2"></Skeleton>
      </div>
    </div>
    <div class="col-12">
      <Divider />
    </div>
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <Skeleton width="100%" height="400px"></Skeleton>
    </div>
    <div class="col-12">
      <Divider />
    </div>
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <div class="flex flex-column md:col-6">
        <Skeleton width="100%" height="5rem" class="mb-2"></Skeleton>
      </div>
    </div>
  </div>
  <div v-else>
    <Step :step-number="1" title="Alcance del grupo de documentos">
      <div class="flex flex-column">
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          @update:visible="updateDialogVisibility"
          :options="payrolls"
          v-model="selectedPayrolls"
          :disabled="isDisabled"
          show-key="name"
          value-key="id"
          :required="true"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
    </Step>
    <Divider />
    <Step :step-number="2" title="Documentos y politicas">
      <DataTable
        v-model:editingRows="editingRows"
        :value="documentSteps.data"
        editMode="row"
        dataKey="stepNumber"
        @row-edit-save="onRowEditSave"
        tableClass="editable-cells-table"
        tableStyle="min-width: 30rem"
        scrollHeight="600px"
        scrollable
        class="tabla"
      >
        <Column header="#" style="width: 15%">
          <template #body="{ data, index }">
            <Chip
              class="flex justify-content-center w-5 h-2rem"
              :class="{ 'bg-teal-200': data.file !== '' }"
              :label="String(index + 1)"
            />
          </template>
        </Column>
        <Column field="name" header="Nombre" style="width: 20%">
          <template #body="{ data, field }">
            {{ data.name }}
          </template>
        </Column>
        <Column field="type" header="Extensión" style="width: 30%">
          <template #body="{ data, field }">
            <span v-if="data.type">
              {{ data.type }}
            </span>
            <span v-else>
              <label class="text-600 font-medium">Sin asignar</label>
            </span>
          </template>
        </Column>
        <Column field="size" header="Tamaño(kb)" style="max-width: 5%">
          <template #body="{ data, field }">
            <span class="text-green-800">
              {{ Number(data[field] / 1024).toFixed(2) }}
            </span>
          </template>
        </Column>
        <Column field="file" header="Archivo" style="width: 20%">
          <template #body="{ data, field }">
            <Button
              v-if="data.file"
              v-tooltip.bottom="'ver adjunto'"
              class="action-btn view hover:text-blue-800 mr-2"
              severity="secondary"
              icon="pi pi-file"
              @click="downloadAttachment(data.file)"
            ></Button>
          </template>
          <template #editor="{ index }">
            <FileUpload
              mode="basic"
              class="font-light w-12 text-center"
              custom-upload
              @uploader="removeFile(index)"
              @select="saveFile($event, index)"
              size="small"
            />
          </template>
        </Column>
        <Column field="link_1" header="Enlace 1">
          <template #body="{ data, field }">
            <a :href="data.link_1">
              {{ data.link_1 }}
            </a>
          </template>
          <template #editor="{ data, field }">
            <InputText v-model="data[field]"></InputText>
          </template>
        </Column>
        <Column field="link_2" header="Enlace 2">
          <template #body="{ data }">
            <a :href="data.link_2">
              {{ data.link_2 }}
            </a>
          </template>
          <template #editor="{ data, field }">
            <InputText v-model="data[field]"></InputText>
          </template>
        </Column>
        <Column
          :rowEditor="true"
          style="width: 10%; min-width: 8rem"
          bodyStyle="text-align:center"
        ></Column>
        <Column>
          <template #body="{ data, index }">
            <div class="actions">
              <Button
                class="action-btn view p-button-sm"
                icon="pi pi-trash"
                @click="deleteApprovalStep($event, data['stepNumber'])"
                severity="danger"
                v-tooltip.bottom="'Eliminar'"
                :disabled="documentSteps.data.length === index + 1"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </Step>
    <Divider></Divider>
    <Step :step-number="3" title="Descripción del grupo de documentos">
      <div class="flex flex-column md:w-5 h-5">
        <Textarea class="mt-2" style="min-width: 250px" v-model="description" />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
    </Step>
  </div>
  <Footer>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/documentGroups')
      "
    />
    <Button
      label="Guardar"
      class="mr-2 text-sm font-light"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
      :loading="saving"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import Step from '@/shared/components/Step.vue'
  import * as yup from 'yup'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'

  import { useField } from 'vee-validate'
  import useNotifications from '@/store/notification'
  import router from '@/router'
  import DocumentFile from '../models/DocumentFile'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import {
    createDocumentGroup,
    uploadDocumentService,
  } from '../services/DocumentService'
  import UploadDocumentDto from '../services/dtos/UploadDocumentDto'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const path = useRoute()
  const payrollConfigId = ref()
  const notificationStore = useNotifications()
  const isLoading = ref(false)
  const saving = ref(false)

  const payrolls = ref()
  const dialogVisible = ref(false)
  const isDisabled = ref(false) // Set to true to disable the

  //variables used in the second step
  const documentSteps = reactive({
    data: [
      {
        stepNumber: 1,
        type: '',
        name: '',
        size: '',
        file: '',
        link_1: '',
        link_2: '',
      },
    ] as DocumentFile[],
    totalItems: 1,
  })

  const descriptionSchema = yup
    .string()
    .required('La descripción del grupo es requierida')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionada')
    .required('Las nóminas son requeridas')
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<[]>('selectedPayrolls', payrollsSchema)
  const editingRows = ref([])

  onMounted(async () => {
    isLoading.value = true
    payrollConfigId.value = path.params.id
    const payrollReponse = await getPayrollsByConfigId(
      String(payrollConfigId.value)
    )
    payrolls.value = payrollReponse.map((payroll) => ({
      id: payroll.id,
      name: payroll.code + '-' + payroll.name,
    }))
    isLoading.value = false
  })

  const updateDialogVisibility = (value: boolean) => {
    dialogVisible.value = value
  }

  const saveFile = (event: any, index: number) => {
    const file = event.files[0]
    documentSteps.data[index].file = file
    documentSteps.data[index].type = file.type.split('/')[1]
    documentSteps.data[index].name = file.name
    documentSteps.data[index].size = file.size
  }

  const removeFile = (index: number) => {
    documentSteps.data[index].file = ''
  }

  const deleteApprovalStep = (event: any, step: number) => {
    documentSteps.data = documentSteps.data.filter(
      (ds) => ds.stepNumber !== step
    )
  }

  const onSave = async () => {
    saving.value = true
    const isValid = (await descValidate()) && (await payrollsValidate())

    const documentGroupId = await createDocumentGroup({
      description: description.value,
      onlyPayrolls: selectedPayrolls.value ? selectedPayrolls.value : [],
      payrollConfigId: payrollConfigId.value,
    })

    const documentsToUpload: UploadDocumentDto[] = []

    //Delete last step because is empty
    documentSteps.data.pop()

    documentSteps.data.forEach((step) => {
      documentsToUpload.push({
        file: step.file ? step.file : '',
        groupId: documentGroupId,
        link_1: step.link_1,
        link_2: step.link_2,
      })
    })

    const response = await uploadDocumentService(documentsToUpload)
    saving.value = false
    if (response) {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Grupo de documento creado con exito`,
      })

      router.push(
        '/payrollsConfig/' + payrollConfigId.value + '/documentGroups'
      )
    }
  }

  const downloadAttachment = async (file: string | File) => {
    if (typeof file === 'string') {
      const fileUrl = await getFileUrlPresigned(file)
      window.open(fileUrl, '_blank')
    } else {
      //visualize File type
      const fileUrl = URL.createObjectURL(file)
      window.open(fileUrl, '_blank')
    }
  }

  const isValidToSave = computed(() => {
    return documentSteps.data.length > 1 && description.value
  })

  const onRowEditSave = (event: any) => {
    let { newData, index } = event
    documentSteps.data[index] = {
      stepNumber: newData.stepNumber,
      type: documentSteps.data[index].type,
      name: documentSteps.data[index].name,
      size: documentSteps.data[index].size,
      file: documentSteps.data[index].file,
      link_1: newData.link_1,
      link_2: newData.link_2,
    }

    newData = documentSteps.data[index]

    if (
      (newData.link_1 !== '' || newData.link_2 !== '' || newData.file !== '') &&
      index === documentSteps.data.length - 1
    ) {
      const newStepNumber = Number(documentSteps.data[index].stepNumber) + 1
      documentSteps.data.push({
        stepNumber: newStepNumber,
        type: '',
        name: '',
        size: '',
        file: '',
        link_1: '',
        link_2: '',
      })
    }
  }
</script>
